import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, DollarSign, Calendar, Building, FileText, Tag, Upload, X } from 'lucide-react';
import { ExpenseService, Expense, ExpenseUpdate } from '../services/ExpenseService';
import { ExpenseCategoryService, ExpensePolicyService, ExpenseCategory, ExpensePolicy } from '../services/CategoryPolicyService';
import { receiptService } from '../services/ReceiptService';
import { useAuth } from '../contexts/AuthContext';

const ExpenseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expense, setExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState<ExpenseUpdate>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [policies, setPolicies] = useState<ExpensePolicy[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  
  // Receipt upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [receiptUploadError, setReceiptUploadError] = useState<string | null>(null);
  const [receiptUploadSuccess, setReceiptUploadSuccess] = useState(false);
  
  // Receipt editing state
  const [receiptData, setReceiptData] = useState<any>(null);
  const [isEditingReceipt, setIsEditingReceipt] = useState(false);
  const [receiptEditData, setReceiptEditData] = useState<any>({});
  const [isSavingReceipt, setIsSavingReceipt] = useState(false);

  useEffect(() => {
    const fetchExpense = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const expenseData = await ExpenseService.getExpense(parseInt(id));
        setExpense(expenseData);
        
        // Initialize form data
        setFormData({
          title: expenseData.title,
          description: expenseData.description || '',
          amount: expenseData.amount,
          currency: expenseData.currency,
          vendor: expenseData.vendor || '',
          invoice_number: expenseData.invoice_number || '',
          category_id: expenseData.category_id,
          policy_id: expenseData.policy_id
        });

        // Load policies for the current category if it exists
        if (expenseData.category_id) {
          try {
            const policiesForCategory = await ExpensePolicyService.getPoliciesByCategory(expenseData.category_id, user?.org_id || 1);
            setPolicies(policiesForCategory);
          } catch (err) {
            console.error('Error loading policies for category:', err);
            // Fallback to all policies if category-specific loading fails
            const allPolicies = await ExpensePolicyService.getPolicies();
            setPolicies(allPolicies);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch expense');
      } finally {
        setLoading(false);
      }
    };

    fetchExpense();
  }, [id, user?.org_id]);

  // Load categories and policies
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [categoriesData, policiesData] = await Promise.all([
          ExpenseCategoryService.getCategories(),
          ExpensePolicyService.getPolicies()
        ]);
        setCategories(categoriesData);
        setPolicies(policiesData);
      } catch (err: any) {
        console.error('Error loading categories and policies:', err);
        setError('Failed to load categories and policies');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, []);

  // Load receipt data if expense has a receipt
  useEffect(() => {
    const fetchReceiptData = async () => {
      if (!expense?.receipt_id || !user) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const receipt = await receiptService.getReceipt(expense.receipt_id, token);
        setReceiptData(receipt);
        setReceiptEditData({
          extracted_amount: receipt.extracted_amount,
          extracted_date: receipt.extracted_date,
          extracted_vendor: receipt.extracted_vendor,
          extracted_invoice_id: receipt.extracted_invoice_id
        });
      } catch (err) {
        console.error('Error fetching receipt data:', err);
      }
    };

    fetchReceiptData();
  }, [expense?.receipt_id, user]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // If category changes, load policies for that category
    if (name === 'category_id' && value) {
      try {
        const categoryId = parseInt(value);
        const policiesForCategory = await ExpensePolicyService.getPoliciesByCategory(categoryId, user?.org_id || 1);
        setPolicies(policiesForCategory);
        // Reset policy selection when category changes
        setFormData(prev => ({
          ...prev,
          [name]: parseInt(value),
          policy_id: undefined
        }));
      } catch (err) {
        console.error('Error loading policies for category:', err);
        setError('Failed to load policies for selected category');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'amount' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expense) return;
    
    setSaving(true);
    setError('');

    try {
      await ExpenseService.updateExpense(expense.id, formData);
      navigate(`/expenses/${expense.id}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update expense. Please try again.';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setReceiptUploadError(null);
    setReceiptUploadSuccess(false);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setReceiptUploadError(null);
    setReceiptUploadSuccess(false);
  };

  const handleReceiptUpload = async () => {
    if (!selectedFile || !user) return;

    setIsUploadingReceipt(true);
    setReceiptUploadError(null);
    setReceiptUploadSuccess(false);

    try {
      // Validate file
      const validation = receiptService.validateFile(selectedFile);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Upload file
      const result = await receiptService.uploadReceipt(selectedFile, token);
      
      if (result.success && result.receipt_id) {
        // Update the form data with the receipt ID
        setFormData(prev => ({
          ...prev,
          receipt_id: result.receipt_id
        }));
        
        setReceiptUploadSuccess(true);
        setReceiptUploadError(null);
        setSelectedFile(null); // Clear selected file after successful upload
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err: any) {
      setReceiptUploadError(err.message || 'Failed to upload receipt');
      setReceiptUploadSuccess(false);
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const handleReceiptEditChange = (field: string, value: any) => {
    setReceiptEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveReceipt = async () => {
    if (!expense?.receipt_id || !user) return;

    setIsSavingReceipt(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      await receiptService.updateReceipt(expense.receipt_id, receiptEditData, token);
      
      // Refresh receipt data
      const updatedReceipt = await receiptService.getReceipt(expense.receipt_id, token);
      setReceiptData(updatedReceipt);
      setIsEditingReceipt(false);
      
      // Show success message
      setReceiptUploadSuccess(true);
      setTimeout(() => setReceiptUploadSuccess(false), 3000);
      
    } catch (err: any) {
      setReceiptUploadError(err.message || 'Failed to update receipt');
      setTimeout(() => setReceiptUploadError(null), 5000);
    } finally {
      setIsSavingReceipt(false);
    }
  };

  const handleCancelReceiptEdit = () => {
    setIsEditingReceipt(false);
    // Reset to original data
    if (receiptData) {
      setReceiptEditData({
        extracted_amount: receiptData.extracted_amount,
        extracted_date: receiptData.extracted_date,
        extracted_vendor: receiptData.extracted_vendor,
        extracted_invoice_id: receiptData.extracted_invoice_id
      });
    }
  };

  const formatCurrency = (amount: number | string, currency: string) => {
    // Convert string to number if needed
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Handle invalid amounts
    if (numericAmount === null || numericAmount === undefined || isNaN(numericAmount)) {
      return '$0.00';
    }
    
    // Map PYUSD to USD for display purposes since Intl.NumberFormat doesn't recognize PYUSD
    const displayCurrency = currency === 'PYUSD' ? 'USD' : currency;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: displayCurrency,
      }).format(numericAmount);
    } catch (error) {
      // Fallback for any other invalid currency codes
      return `${currency} ${numericAmount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'sent_back':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Expense Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The requested expense could not be found.'}</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if expense can be edited (only draft or sent_back status)
  const canEdit = expense.status === 'draft' || expense.status === 'sent_back';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/expenses/${expense.id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Expense Details
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Expense</h1>
          <p className="mt-2 text-gray-600">Update expense information</p>
        </div>

        {/* Status Warning */}
        {!canEdit && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
            <p className="font-medium">This expense cannot be edited.</p>
            <p className="text-sm">Only expenses with "draft" or "sent_back" status can be modified.</p>
          </div>
        )}

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-2" />
                  Expense Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  disabled={!canEdit}
                  value={formData.title || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="e.g., Business lunch, Office supplies"
                />
              </div>

              <div>
                <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-2" />
                  Vendor
                </label>
                <input
                  type="text"
                  id="vendor"
                  name="vendor"
                  disabled={!canEdit}
                  value={formData.vendor || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="e.g., Starbucks, Amazon"
                />
              </div>
            </div>

            {/* Amount and Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="h-4 w-4 inline mr-2" />
                  Amount *
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  required
                  min="0"
                  step="0.01"
                  disabled={!canEdit}
                  value={formData.amount || 0}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  disabled={!canEdit}
                  value={formData.currency || 'USD'}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="SOL">SOL</option>
                  <option value="PYUSD">PYUSD</option>
                </select>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice/Receipt Number
                </label>
                <input
                  type="text"
                  id="invoice_number"
                  name="invoice_number"
                  disabled={!canEdit}
                  value={formData.invoice_number || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="e.g., INV-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Expense Date & Time
                </label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {formatDate(expense.time)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Date cannot be changed</p>
              </div>
            </div>

            {/* Category and Policy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="h-4 w-4 inline mr-2" />
                  Category
                </label>
                <select
                  id="category_id"
                  name="category_id"
                  disabled={!canEdit}
                  value={formData.category_id || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="policy_id" className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-2" />
                  Policy
                </label>
                <select
                  id="policy_id"
                  name="policy_id"
                  disabled={!canEdit}
                  value={formData.policy_id || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a policy</option>
                  {policies.map((policy) => (
                    <option key={policy.id} value={policy.id}>
                      {policy.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                disabled={!canEdit}
                value={formData.description || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Additional details about this expense..."
              />
            </div>

            {/* Current Status Display */}
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Current Status</h3>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(expense.status)}`}>
                      {expense.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(expense.amount, expense.currency)}
                  </p>
                </div>
              </div>
            </div>

            {/* Receipt Upload Section */}
            {canEdit && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Attach Receipt
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a receipt to support this expense. This is especially useful for crypto-created expenses.
                </p>
                
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {selectedFile ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <FileText className="h-8 w-8 text-green-600 mr-3" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-center space-x-3">
                        <button
                          type="button"
                          onClick={handleReceiptUpload}
                          disabled={isUploadingReceipt}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {isUploadingReceipt ? 'Uploading...' : 'Upload Receipt'}
                        </button>
                        <button
                          type="button"
                          onClick={handleFileRemove}
                          disabled={isUploadingReceipt}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                      <div>
                        <label className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Choose a file to upload
                          </span>
                          <span className="mt-1 block text-sm text-gray-500">
                            or drag and drop here
                          </span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*,.pdf"
                            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, PDF up to 10MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Upload Status Messages */}
                {receiptUploadError && (
                  <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    <p className="text-sm">{receiptUploadError}</p>
                  </div>
                )}

                {receiptUploadSuccess && (
                  <div className="mt-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                    <p className="text-sm">Receipt uploaded successfully! It will be attached when you save the expense.</p>
                  </div>
                )}

              </div>
            )}

            {/* Receipt Editing Section - Available regardless of expense status */}
            {expense.receipt_id && receiptData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-blue-900">Receipt Information</h4>
                  <button
                    type="button"
                    onClick={() => setIsEditingReceipt(!isEditingReceipt)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {isEditingReceipt ? 'Cancel Edit' : 'Edit Receipt'}
                  </button>
                </div>
                
                {isEditingReceipt ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={receiptEditData.extracted_amount || ''}
                          onChange={(e) => handleReceiptEditChange('extracted_amount', parseFloat(e.target.value) || null)}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter amount"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Date</label>
                        <input
                          type="date"
                          value={receiptEditData.extracted_date ? receiptEditData.extracted_date.split('T')[0] : ''}
                          onChange={(e) => handleReceiptEditChange('extracted_date', e.target.value ? new Date(e.target.value).toISOString() : null)}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Vendor</label>
                        <input
                          type="text"
                          value={receiptEditData.extracted_vendor || ''}
                          onChange={(e) => handleReceiptEditChange('extracted_vendor', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter vendor name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-blue-700 mb-1">Invoice Number</label>
                        <input
                          type="text"
                          value={receiptEditData.extracted_invoice_id || ''}
                          onChange={(e) => handleReceiptEditChange('extracted_invoice_id', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter invoice number"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={handleCancelReceiptEdit}
                        className="px-3 py-1 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveReceipt}
                        disabled={isSavingReceipt}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingReceipt ? 'Saving...' : 'Save Receipt'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-blue-700">Amount:</span>
                      <span className="ml-2 text-blue-900">
                        {receiptData.extracted_amount ? `$${Number(receiptData.extracted_amount).toFixed(2)}` : 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Date:</span>
                      <span className="ml-2 text-blue-900">
                        {receiptData.extracted_date ? new Date(receiptData.extracted_date).toLocaleDateString() : 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Vendor:</span>
                      <span className="ml-2 text-blue-900">
                        {receiptData.extracted_vendor || 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-blue-700">Invoice:</span>
                      <span className="ml-2 text-blue-900">
                        {receiptData.extracted_invoice_id || 'Not specified'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/expenses/${expense.id}`)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !canEdit}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseEditPage;
